import { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { userLogin } from '@/store/auth';
import { resetIntent } from '@/store/billing';

import AppButton from '@/components/AppButton';
import AppLink from '@/components/AppLink';
import AppCard from '@/components/AppCard';
import AppBox from '@/components/AppBox';
import AppInput from '@/components/AppInput';

interface FormState {
  email: string;
  password: string;
}

interface ErrorsState {
  email: string;
  password: string;
}

const Login = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // Use RootState type for type safety
  const status = useAppSelector((state) => state.auth.status);

  // State for form and errors
  const [form, setForm] = useState<FormState>({ email: '', password: '' });
  const [errors, setErrors] = useState<ErrorsState>({ email: '', password: '' });

  // Reset intent on component mount
  useEffect(() => {
    dispatch(resetIntent());
  }, [dispatch]);

  // Handle form submission
  const handleSubmit = async () => {
    // Validate fields
    const newErrors: ErrorsState = { email: '', password: '' };
    let hasError = false;

    Object.keys(form).forEach((field) => {
      if (!form[field as keyof FormState]) {
        hasError = true;
        newErrors[field as keyof ErrorsState] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
      }
    });

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    // Dispatch login action
    dispatch(userLogin({ email: form.email, password: form.password })).unwrap().then((data) => {
      if (data.is_agency_admin) {
        navigate('/agency');
      }
    });
  };

  // Handle field changes
  const handleChange = (field: keyof FormState) => (value: string) => {
    if (errors[field]) {
      setErrors((state) => ({
        ...state,
        [field]: '',
      }));
    }
    setForm((state) => ({
      ...state,
      [field]: value,
    }));
  };

  return (
    <AppCard>
      <AppBox column gap={6}>
        <AppBox column gap={2}>
          <AppInput
            type="email"
            label="Email Address"
            fullWidth
            onChange={handleChange('email')}
            value={form.email}
            autoFocus={true}
            error={errors.email}
            onPressEnter={handleSubmit}
          />

          <AppInput
            type="password"
            label="Password"
            fullWidth
            onChange={handleChange('password')}
            value={form.password}
            error={errors.password}
            onPressEnter={handleSubmit}
          />
        </AppBox>

        <AppButton onClick={handleSubmit} isLoading={status === 'loading'}>
          Log In
        </AppButton>

        <AppBox justify="between">
          <AppLink to="/register">Sign Up</AppLink>
          <AppLink to="/recovery">Forgot password?</AppLink>
        </AppBox>
      </AppBox>
    </AppCard>
  );
};

export default Login;